// Lead gönderimi — tek uç, tek yer.
//
// Web3Forms formu e-postaya iletir; ayrı bir backend yok. Sitedeki bütün
// formlar buradan geçer, hangisinden geldiği `formName` ile ayrılır.

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';

/**
 * Lead'in düşeceği e-posta kutuları.
 *
 * Web3Forms'ta bir access key = bir e-posta adresi. Birden fazla adrese
 * göndermenin iki yolu var:
 *
 *   1. ÜCRETSİZ (buradaki yol): her adres için web3forms.com'dan ayrı bir
 *      access key alınır ve bu diziye eklenir. Lead her anahtara ayrı ayrı
 *      POST edilir. Dizinin İLK anahtarı birincil kutudur — kullanıcıya
 *      "gönderildi" demek için onun başarılı olması yeterli, diğerleri
 *      arka planda denenir.
 *
 *   2. PRO (ücretli): tek anahtar + payload'a `ccemail` alanı
 *      ("a@x.com; b@y.com" — noktalı virgülle ayrılır). Ücretsiz planda
 *      bu alan yok sayılır, o yüzden burada kullanılmıyor.
 *
 * Not: e-posta sağlayıcısında yönlendirme kuralı kurmak da işi görür ve
 * hiç kod gerektirmez.
 */
const WEB3FORMS_KEYS = [
  // info@meetingayrimenkul.com
  // Teslim adresi kodda değil, web3forms.com hesabında tanımlı — buradaki not
  // 2 Eylül 2026'da gerçek bir test gönderimiyle doğrulandı. Anahtar
  // değiştirilirse bu not da yeniden doğrulanmalı.
  '5e3ee22f-019e-422b-943d-8cfac4fe918c',
  // 'İKİNCİ-ANAHTAR-BURAYA', // ikinci adres — anahtar alınınca aç
];

/** En az bir anahtar tanımlıysa formlar Web3Forms'a gider, değilse mailto'ya düşer. */
export const LEAD_DELIVERY_CONFIGURED = WEB3FORMS_KEYS.length > 0;

export type LeadPayload = {
  /** Hangi form — e-posta konusunda ve GTM'de ayırt etmek için. */
  formName: string;
  firstName: string;
  lastName: string;
  phone: string;
  /** Seçilen daire tipi (ör. "3+1"). Boş olabilir. */
  plan?: string;
  email?: string;
  message?: string;
  /** Ticari ileti (SMS/arama) izni verildi mi — KVKK açık rızası, opsiyonel. */
  marketingConsent?: boolean;
  /** URL'den yakalanan utm_* / gclid / fbclid alanları. */
  attribution?: Record<string, string>;
};

function buildBody(lead: LeadPayload, accessKey: string) {
  const adSoyad = `${lead.firstName} ${lead.lastName}`.trim();
  return JSON.stringify({
    access_key: accessKey,
    subject: `Rain Nova — Yeni Lead (${lead.formName})`,
    from_name: adSoyad || 'Rain Nova Site',
    ad_soyad: adSoyad,
    telefon: lead.phone,
    email: lead.email || '(verilmedi)',
    daire_tipi: lead.plan || '(seçilmedi)',
    mesaj: lead.message || '(boş)',
    form: lead.formName,
    ticari_ileti_izni: lead.marketingConsent ? 'EVET' : 'HAYIR',
    ...lead.attribution,
    // Honeypot — botlar doldurur, gerçek kullanıcı görmez
    botcheck: '',
  });
}

async function postTo(accessKey: string, lead: LeadPayload): Promise<void> {
  const res = await fetch(WEB3FORMS_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: buildBody(lead, accessKey),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.message || 'Gönderim başarısız');
}

/**
 * Lead'i tanımlı bütün kutulara gönderir. Birincil kutu (dizinin ilk anahtarı)
 * başarısız olursa hata fırlatır; ikincil kutular sessizce loglanır — bir kopya
 * ulaşmadı diye kullanıcıya form yeniden doldurtmanın anlamı yok.
 *
 * Çağıran taraf dönüşüm olayını (generate_lead) YALNIZCA bu söz çözüldüğünde
 * push etmeli, yoksa Ads/GA4 tarafında olmayan lead sayılır.
 */
export async function submitLead(lead: LeadPayload): Promise<void> {
  const [primary, ...secondaries] = WEB3FORMS_KEYS;
  if (!primary) throw new Error('Web3Forms anahtarı tanımlı değil');

  const rest = Promise.allSettled(secondaries.map((key) => postTo(key, lead)));
  await postTo(primary, lead);

  rest.then((results) => {
    results.forEach((r, i) => {
      if (r.status === 'rejected') {
        console.warn(`Lead ikincil kutuya iletilemedi (anahtar #${i + 2}):`, r.reason);
      }
    });
  });
}
